import { formatCurrency } from "@tellescope/utils";
import type { ShopPriceVariant } from "./types";

/**
 * The abbreviation itself doesn't change with intervalCount in this compact format (a "3mo" suffix
 * next to "$480.00" reads clearly without pluralizing to "3mos"), so this only needs one translated
 * form per unit, not i18next's `_one`/`_other` split — unlike a natural-language sentence rendering
 * of the same interval (e.g. "day"/"week"/"month"/"year"/{{count}} would need real pluralization).
 * Deliberately its own translated abbreviation set rather than reusing
 * `@tellescope/utils`'s `formatStripeSubscriptionInterval` — that util renders full words
 * ("/3 months"), a different, intentionally more compact display convention shop uses everywhere
 * prices render (product cards, cart rows), not a gap this file forgot to consolidate.
 * The `{ ns: "shop" }` on every call is redundant at runtime (every caller's `t` is already bound to
 * the shop namespace) but is what i18next-cli's static extractor keys off — it can't trace a `t`
 * passed in as a plain function parameter back to its originating namespace, so without this these
 * keys land in the default `common` namespace on every extract/sync pass.
 */
const getIntervalUnit = (
  t: (key: string, options: { ns: "shop" }) => string
): Record<NonNullable<ShopPriceVariant["interval"]>, string> => ({
  day: t("day", { ns: "shop" }),
  week: t("wk", { ns: "shop" }),
  month: t("mo", { ns: "shop" }),
  year: t("yr", { ns: "shop" }),
});

// Stripe has no separate "quarter"/"semi-annual" interval — those are expressed as interval:
// "month" with intervalCount 3/6. A count of 1 renders as the short suffix ("/mo"); any other
// count spells out the multiple ("/3mo") so e.g. monthly and quarterly variants of the same
// product are visually distinguishable, not both shown as "/mo". Exported so aggregate totals
// (groupSubscriptionTotals) can label a summed cadence bucket the same way a single variant's own
// price is labeled here, instead of re-deriving the suffix format separately.
export const formatIntervalSuffix = (
  t: (key: string, options: { ns: "shop" }) => string,
  interval: NonNullable<ShopPriceVariant["interval"]>,
  intervalCount = 1
): string => {
  const unit = getIntervalUnit(t)[interval];
  return intervalCount > 1 ? `/${intervalCount}${unit}` : `/${unit}`;
};

/** Formats a shop price variant's snapshotted amount (smallest currency unit) as a display price. */
export const formatShopPrice = (
  t: (key: string, options: { ns: "shop" }) => string,
  variant: ShopPriceVariant
): string => {
  const suffix =
    variant.isSubscription && variant.interval ? formatIntervalSuffix(t, variant.interval, variant.intervalCount) : "";
  return `${formatCurrency(variant.amount, variant.currency, { fromSmallestUnit: true })}${suffix}`;
};
